import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { BookingNotificationTrigger, type EventTypeNotification, type EventTypeReminder, type SchedulingEventType } from '../../../../../models/Scheduling'
import { TemplatePreviewModal } from '../TemplatePreviewModal'
import { useSchedulingEmailTemplates } from '../../../../Scheduling/shared/useSchedulingEmailTemplates'

interface RemindersTabProps {
  draft: SchedulingEventType
  disabled: boolean
  onDraftChange: (patch: Partial<SchedulingEventType>) => void
}

const TIMING_OPTIONS = [1, 2, 24, 48, 72]
const timingLabel = (hours: number): string => `${hours} ${hours === 1 ? 'hour' : 'hours'} before`

const TRIGGER_OPTIONS: { value: BookingNotificationTrigger; label: string }[] = [
  { value: BookingNotificationTrigger.BOOKING_MADE, label: 'When a booking is made' },
  { value: BookingNotificationTrigger.BOOKING_RESCHEDULED, label: 'When a booking is rescheduled' },
  { value: BookingNotificationTrigger.BOOKING_CANCELLED, label: 'When a booking is cancelled' },
]

export const RemindersTab: FC<RemindersTabProps> = ({ draft, disabled, onDraftChange }) => {
  const [previewTemplateId, setPreviewTemplateId] = useState<string | undefined>(undefined)
  const { findById } = useSchedulingEmailTemplates()
  const { reminders, notifications } = draft

  const updateReminder = (index: number, update: Partial<EventTypeReminder>) => {
    onDraftChange({ reminders: reminders.map((reminder, reminderIndex) => (reminderIndex === index ? { ...reminder, ...update } : reminder)) })
  }

  const updateNotification = (index: number, update: Partial<EventTypeNotification>) => {
    onDraftChange({
      notifications: notifications.map((notification, notificationIndex) => (notificationIndex === index ? { ...notification, ...update } : notification)),
    })
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <div>
          <div className='lead-small zocks-text-gray-500 text-uppercase'>Client reminders</div>
          <div className='paragraph-small zocks-text-gray-600'>Nudge clients before the meeting so fewer bookings are missed.</div>
        </div>
        {reminders.map((reminder, index) => (
          <div key={index} className='d-flex align-items-center gap-2'>
            <i className='bi bi-grip-vertical zocks-text-gray-500 flex-shrink-0' />
            <Form.Select
              className='w-auto flex-shrink-0'
              value={reminder.offsetMinutes / 60}
              disabled={disabled}
              aria-label={`Reminder ${index + 1} timing`}
              onChange={(event) => updateReminder(index, { offsetMinutes: Number(event.currentTarget.value) * 60 })}
            >
              {TIMING_OPTIONS.map((hours) => (
                <option key={hours} value={hours}>
                  {timingLabel(hours)}
                </option>
              ))}
            </Form.Select>
            <span className='paragraph-small zocks-text-gray-600 flex-shrink-0'>send</span>
            <div className='d-flex align-items-center justify-content-between gap-2 flex-grow-1 border rounded-2 zocks-border-color-gray-300 zocks-px-1.25 py-2'>
              <span className='d-flex align-items-center zocks-gap-0.5 text-truncate'>
                <i className='bi bi-file-earmark-text zocks-text-gray-600' />
                <span className='text-truncate'>{findById(reminder.emailTemplateId)?.name ?? 'Reminder'}</span>
                <i className='bi bi-send zocks-text-gray-500' />
              </span>
              <Button
                variant='grayGhost'
                className='p-1 flex-shrink-0'
                aria-label={`Preview reminder ${index + 1} template`}
                onClick={() => setPreviewTemplateId(reminder.emailTemplateId)}
              >
                <i className='bi bi-pencil' />
              </Button>
            </div>
            {!disabled ? (
              <Button
                variant='grayGhost'
                className='p-2 flex-shrink-0'
                aria-label={`Remove reminder ${index + 1}`}
                onClick={() => onDraftChange({ reminders: reminders.filter((_, reminderIndex) => reminderIndex !== index) })}
              >
                <i className='bi bi-x-lg' />
              </Button>
            ) : null}
          </div>
        ))}
        {!disabled ? (
          <Button
            variant='link'
            className='p-0 align-self-start'
            onClick={() => onDraftChange({ reminders: [...reminders, { offsetMinutes: 24 * 60, emailTemplateId: '' }] })}
          >
            <i className='bi bi-plus-circle me-1' />
            Add reminder
          </Button>
        ) : null}
      </div>
      <div className='d-flex flex-column gap-3'>
        <div>
          <div className='lead-small zocks-text-gray-500 text-uppercase'>Notifications to you</div>
          <div className='paragraph-small zocks-text-gray-600'>Get an email when bookings change so you stay in the loop.</div>
        </div>
        {notifications.map((notification, index) => (
          <div key={index} className='d-flex flex-column gap-2 p-3 rounded-2 zocks-bg-color-gray-100'>
            <div className='d-flex align-items-center gap-2'>
              <Form.Select
                className='flex-grow-1'
                value={notification.trigger}
                disabled={disabled}
                aria-label={`Notification ${index + 1} trigger`}
                onChange={(event) => updateNotification(index, { trigger: event.currentTarget.value as BookingNotificationTrigger })}
              >
                {TRIGGER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              {!disabled ? (
                <Button
                  variant='grayGhost'
                  className='p-2 flex-shrink-0'
                  aria-label={`Remove notification ${index + 1}`}
                  onClick={() => onDraftChange({ notifications: notifications.filter((_, notificationIndex) => notificationIndex !== index) })}
                >
                  <i className='bi bi-x-lg' />
                </Button>
              ) : null}
            </div>
            <Form.Control
              type='email'
              value={notification.email}
              disabled={disabled}
              placeholder='Your email address'
              aria-label={`Notification ${index + 1} email`}
              onChange={(event) => updateNotification(index, { email: event.currentTarget.value })}
            />
            <Form.Control
              type='email'
              value={notification.alsoNotify ?? ''}
              disabled={disabled}
              placeholder='Also notify (optional)'
              aria-label={`Notification ${index + 1} additional email`}
              onChange={(event) => updateNotification(index, { alsoNotify: event.currentTarget.value })}
            />
          </div>
        ))}
        {!disabled ? (
          <Button
            variant='link'
            className='p-0 align-self-start'
            onClick={() =>
              onDraftChange({ notifications: [...notifications, { trigger: BookingNotificationTrigger.BOOKING_MADE, email: '', alsoNotify: '' }] })
            }
          >
            <i className='bi bi-plus-circle me-1' />
            Add notification
          </Button>
        ) : null}
      </div>
      {previewTemplateId ? <TemplatePreviewModal template={findById(previewTemplateId)} onClose={() => setPreviewTemplateId(undefined)} /> : null}
    </div>
  )
}
